import { TransferItemAPITypes } from '@app/transfer-bundle/constant/transfer-item-types';
import { TransferConfig } from '@app/transfer-bundle/config/transfer-config';

// Private
let imageCounter = 0;

// This calculates the chunkSize needed for this file to make sure
// the total amount of chunks stays below the sizePerChunk.
function calculateChunkSize(filesize) {
  const sizePerChunk = Math.ceil(
    filesize / TransferConfig.upload.chunk.maxCount
  );
  return Math.round(
    TransferConfig.upload.chunk.minSize *
      Math.ceil(sizePerChunk / TransferConfig.upload.chunk.minSize)
  );
}

// This method calculates how many chunks of chunkSize
// are needed to upload this file.
function calculateChunkCount(filesize, chunkSize, apiType) {
  if (apiType === TransferItemAPITypes.emptyDirectory) {
    return 0;
  }

  // even empty files must be "uploaded"
  // there must be a request to S3
  // that's why there must be at least 1 chunk
  return Math.ceil(filesize / chunkSize) || 1;
}

// This method is called during init and reset to
// pretty much just prepare this file for uploading
function resetState(file) {
  file.state = {
    blocked: false,
    bytesSent: 0,
    bytesProgress: [],
    chunksPending: [...Array(file.chunks).keys()],
  };
}

// Return a textual representation of the file
// that is being added. For now just extension.
function determineType(file) {
  // image/*
  // video/*
  // audio/*
  // text/*
  // other

  let ext = file.name;
  ext = ext.substring(ext.lastIndexOf('.') + 1);
  if (!ext) {
    return 'Unknown file';
  }
  return ext.toUpperCase();
}

// Public
export default class TransferItem {
  constructor(original) {
    this.id = original.id || null;
    this.ref = original;
    this.name = original.relativePath || original.name || original.filename;
    this.type = determineType(this);
    this.apiType = original.apiType || TransferItemAPITypes.file;
    this.isFile = this.apiType === TransferItemAPITypes.file;
    this.size = original.size || original.filesize;
    this.safename = this.name.trim();
    this.previewable = original.previewable;
    this.metadata = this.ref.metadata || {};

    // Fix filesize (0 is now treated as undefined)
    if (!this.size) {
      this.size = 0;
    }

    // So, yeah.. iOS is stupid. Or cool, because privacy. I think cool, it's just annoying.
    let extension;
    if (
      !this.id &&
      navigator.userAgent.match(/(iPad|iPhone|iPod)/i) &&
      (extension = this.safename.match(/\.(jpe?g|png|gif|bmp|tiff?)$/i))
    ) {
      this.name = this.safename =
        'image' + `00000${++imageCounter}`.substr(-5) + '.' + extension[1];
    }

    this.chunkSize = calculateChunkSize(this.size);
    this.chunks = calculateChunkCount(this.size, this.chunkSize, this.apiType);
    this.multipart = {
      numberOfChunks: this.chunks,
      chunkSize: this.chunkSize,
    };

    this.state = {};
    resetState(this);
  }

  static resetFilenameCounter() {
    imageCounter = 0;
  }

  get bytesSent() {
    return (
      this.state.bytesSent +
      this.state.bytesProgress.reduce((bytes, num) => bytes + num, 0)
    );
  }

  didFileSizeChange() {
    return !(this.validateSize() && this.validateSentBytes());
  }

  validateSentBytes() {
    return this.state.bytesSent <= this.size;
  }

  // Make sure filesize hasn't changed since file has been added.
  validateSize() {
    return (
      this.size ===
      (isFinite(this.ref.size) ? this.ref.size : this.ref.filesize)
    );
  }

  block() {
    this.state.blocked = true;
  }

  unblock() {
    this.state.blocked = false;
  }

  // Get the next pending chunk from the list
  nextChunk() {
    // Don't return a chunk if we are blocking this file
    if (this.state.blocked) {
      return false;
    }

    // Get the next chunk, if any
    const next = this.state.chunksPending.shift();
    if (typeof next === 'undefined') {
      return false;
    }
    return next;
  }

  chunkUploaded(chunk) {
    // Do some byte math.
    this.state.bytesSent += chunk.content.size;

    // Clear the buffer to free some memory
    this.buffer = null;

    delete this.state.bytesProgress[chunk.partNumber];
  }

  chunkAborted(chunk) {
    this.state.bytesProgress[chunk.partNumber] = 0;
  }

  // When we need to retry a chunk we will prepend it
  // to the pending list (unless it is already there)
  resetChunk(chunkNumber) {
    if (typeof chunkNumber === 'undefined') {
      return;
    }

    if (this.state.chunksPending.indexOf(chunkNumber) !== -1) {
      return;
    }

    this.state.bytesProgress[chunkNumber] = 0;
    this.state.chunksPending.unshift(chunkNumber);
  }

  // Make sure we still have pending chunks or not
  hasRemainingChunks() {
    return !!this.state.chunksPending.length;
  }

  // Reset this file state and ID, probably a failed transfer
  reset() {
    this.id = null;
    resetState(this);
  }

  // Nice representation
  toString() {
    return `[file name=${this.name} size=${this.size} chunks=${this.multipart.numberOfChunks} chunkSize=${this.multipart.chunkSize}]`;
  }
}
